import { h, render, Fragment } from 'preact';
import { Swiper, SwiperSlide } from 'swiper/react';
// eslint-disable-next-line import/no-unresolved
import SwiperCore, { Pagination, EffectFade } from 'swiper/core';
import { useState, useCallback, useLayoutEffect } from 'preact/hooks';
import Image from '../utils/image';
import BuildIcon from '../utils/icons/buildicon.svg';
import { useWindowSize } from '../utils/window-size';
import Video from '../utils/vimeoPlayer';

SwiperCore.use([Pagination, EffectFade]);

const pagination = (id) => ({
  el: '.custom-pagination',
  clickable: true,
  renderBullet(index, className) {
    return `<button tabindex={0} class="${className}" data-heatmap-id="hero-slider-navigation-${id}-${index}" aria-label="Go to slide ${
      index + 1
    }"></button>`;
  },
});

export default (element, data) => {
  const init = () => {
    render(<Hero />, element);
  };

  const Hero = () => {
    const { isTablet } = useWindowSize();
    const image = isTablet ? 'image_mobile' : 'image_desktop';
    const video = isTablet ? 'video_mobile' : 'video_desktop';
    const autoplay = isTablet
      ? 'video_autoplay_mobile'
      : 'video_autoplay_desktop';
    const controlsPositionDesktop = 'controls_position_desktop';
    const controlsPositionMobile = 'controls_position_mobile';
    const [vimeoPlayer, setVimeoPlayer] = useState(null);

    const onPlayerInit = useCallback((player) => {
      setVimeoPlayer(player);
    }, []);

    useLayoutEffect(() => {
      vimeoPlayer?.play();
    }, [vimeoPlayer]);

    const paginationElement = (
      <div className="custom-pagination !absolute !bottom-[20px] laptop:!bottom-auto -translate-x-1/2 laptop:translate-x-0 laptop:-translate-y-1/2 !left-[50%] laptop:!left-auto !top-auto laptop:!top-[calc(50%+100px)] !right-auto laptop:!right-[40px] laptop:rotate-90 z-10 !w-auto !flex origin-[100%_0%]" />
    );

    return (
      <>
        {!isTablet && paginationElement}
        <Swiper
          slidesPerView={1}
          modules={[Pagination, EffectFade]}
          pagination={pagination(data?.id)}
          spaceBetween={0}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          className="animate-fadein"
        >
          <div className="custom-pagination !absolute !bottom-[20px] laptop:!bottom-auto -translate-x-1/2 laptop:translate-x-0 laptop:-translate-y-1/2 !left-[50%] laptop:!left-auto !top-auto laptop:!top-[calc(50%+100px)] !right-auto laptop:!right-[40px] laptop:rotate-90 z-10 !w-auto !flex origin-[100%_0%]" />
          {data.slides.map((slide, index) => (
            <SwiperSlide
              key={index}
              className={`relative`}
              data-heatmap-id={`hero-slide-${index}`}
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`min-h-[calc(100svh-149px)] xs-h:min-h-[350px] laptop:min-h-[calc(100vh-113px)] ${
                      isActive ? 'block' : 'hidden'
                    }`}
                  >
                    {slide.cta_url_1 && slide.cta_url_2.length === 0 ? (
                      <a
                        href={slide.cta_url_1}
                        className="w-full"
                        tabIndex={isActive ? 0 : -1}
                      >
                        {slide[video] && (
                          <Video
                            url={slide[video]}
                            id={slide[video].split('https://vimeo.com/')[1]}
                            autoplay={slide[autoplay]}
                            muted={true}
                            showControls={true}
                            className={
                              'pt-[calc(100svh-149px)] xs-h:pt-[350px] laptop:pt-[calc(100vh-113px)] video-absolute video-hero'
                            }
                            onPlayerInit={onPlayerInit}
                            loop={true}
                            forcePaused={!isActive}
                            controlsPosition={slide[controlsPositionDesktop]}
                            controlsPositionMobile={
                              slide[controlsPositionMobile]
                            }
                          />
                        )}
                        {slide[image]?.src && (
                          <Image
                            src={slide[image].src}
                            className="block object-cover w-full max-h-[calc(100svh-149px)] xs-h:max-h-[350px] laptop:max-h-[calc(100vh-113px)] absolute h-full"
                            width={slide[image].width}
                            height={slide[image].height}
                            alt={slide[image].alt ?? 'Slide image'}
                            loading={index === 0 ? 'eager' : 'lazy'}
                          />
                        )}
                      </a>
                    ) : (
                      <>
                        {slide[video] && (
                          <Video
                            url={slide[video]}
                            id={slide[video].split('https://vimeo.com/')[1]}
                            autoplay={slide[autoplay]}
                            muted={true}
                            showControls={true}
                            className={
                              'pt-[calc(100svh-149px)] xs-h:pt-[350px] laptop:pt-[calc(100vh-113px)] video-absolute video-hero'
                            }
                            onPlayerInit={onPlayerInit}
                            loop={true}
                            forcePaused={!isActive}
                            controlsPosition={slide[controlsPositionDesktop]}
                            controlsPositionMobile={
                              slide[controlsPositionMobile]
                            }
                          />
                        )}
                        {slide[image]?.src && (
                          <Image
                            src={slide[image].src}
                            className="block object-cover w-full max-h-[calc(100svh-149px)] laptop:max-h-[calc(100vh-113px)] xs-h:max-h-[350px] absolute h-full"
                            width={slide[image].width}
                            height={slide[image].height}
                            alt={slide[image].alt ?? 'Slide image'}
                            loading={index === 0 ? 'eager' : 'lazy'}
                          />
                        )}
                      </>
                    )}
                  </div>
                  <a href={slide.cta_url_1} tabIndex={isActive ? 0 : -1}>
                    <div
                      className={`absolute ${slide.content_alignment_desktop}`}
                    >
                      <div
                        className={`max-w-[600px] ${slide.content_alignment_desktop_original}`}
                      >
                        <h2
                          className={`heading-3 laptop:heading-2 ${slide.content_color_desktop} mb-3 laptop:mb-5`}
                        >
                          {slide.heading}
                        </h2>
                        <p
                          className={`font-r-14 hidden laptop:block ${
                            slide.content_color_desktop
                          } mb-5 ${
                            slide.content_alignment_desktop_original ===
                              'bottomsides' && 'desktop:mb-0'
                          }`}
                        >
                          {slide.content}
                        </p>
                      </div>
                      <div className="flex flex-col justify-center w-full laptop:flex-row laptop:w-auto">
                        {slide.cta_url_1 && slide.cta_text_1 && (
                          <a
                            href={slide.cta_url_1}
                            className="btn-primary-fill flex items-center justify-center mb-[7px] laptop:mb-0 w-full laptop:w-[300px]"
                            tabIndex={isActive ? 0 : -1}
                          >
                            {slide.cta_text_1}
                          </a>
                        )}
                        {slide.cta_url_2 && (
                          <a
                            href={slide.cta_url_2}
                            className="btn-primary-fill bg-primary-2 text-primary-1 w-full laptop:w-[300px]"
                            tabIndex={isActive ? 0 : -1}
                          >
                            Customize
                            <BuildIcon
                              viewBox={`0 0 49 36`}
                              className={`inline-block align-middle !h-auto w-[23px] ml-2.5`}
                            />
                          </a>
                        )}
                      </div>
                    </div>
                  </a>
                </>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        {isTablet && paginationElement}
      </>
    );
  };

  init();
};
